import React from "react"
import PropTypes from "prop-types"
import withImmutablePropsToJS from "with-immutable-props-to-js"

// components
import EnumSelect from "app/static/frontend/selects/components/EnumSelect"

// helpers
import { isRegionEnabled } from "shared/region-selection/helperFunctions"
import { checkHasAllPermissions, permissionFilter } from "shared/imports/permissionFunctions"
import { sortBySearchOrder } from "shared/imports/sharedHelperFunctions"
import { getBillYearSelectOptions, getElectionYearSelectOptions } from "shared/search/constants"

// constants
import Fonts from "app/static/frontend/imports/all_fonts"

// helpers
import { generateElectionYearEnumItems } from "shared/search/helperFunctions"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

export const genericEnumSelect = ({ customOptions, options, label, placeholder, onChange, ...props }) => (
    <EnumSelect
        {...props}
        options={customOptions || options}
        label={label}
        placeholder={placeholder}
        onChange={onChange}
    />
)
genericEnumSelect.propTypes = {
    options: PropTypes.object.isRequired,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
}
export default genericEnumSelect

// this comment is added after this component has been written
// this filter implicitly removes items that are falsey
const identityFilter = (item) => item
export const AbstractEnumSelect = withImmutablePropsToJS(({ options, ...props }) =>
    genericEnumSelect({
        options: options.items ? options.items().filter(identityFilter) : options,
        label: AbstractEnumSelect.defaultProps.label,
        ...props,
    }),
)
AbstractEnumSelect.defaultProps = {
    allowExpandUp: false,
    label: "label",
}

/**
 * This function generates an enum select. This function is distinct from AbstractEnumSelect
 * because we would like to filter the options passed to the Select by some criteria
 * @param {Array[Objects]} filteredOptions - an array of enum objects to pass to the Select
 * @returns A Select generated by genericEnumSelect
 */
export const AbstractEnumSelectObjectOptions = ({ filteredOptions, field, overrideLabel, ...props }) =>
    genericEnumSelect({
        options: filteredOptions,
        label: overrideLabel || (field && field.sheets_display_key) || "label",
        ...props,
    })

export const exampleClassRelationshipTypeSelect = (props) => genericEnumSelect({ ...props })
exampleClassRelationshipTypeSelect.defaultProps = {
    options: DjangIO.app.userdata.types.RelationshipType.items()
        .filter((item) => item.key !== "none")
        .map((item) => ({
            ...item,
            display_text: `${item.third_person} ${item.divider}`,
        })),
    placeholder: "Select relationship type...",
    label: "display_text",
}

export const RelationshipTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.userdata.types.RelationshipType.items()
            .filter((item) => item.key !== "none")
            .sort((a, b) => a.order - b.order)
            .map((item) => ({
                ...item,
                display_text: item.grouped,
            })),
        placeholder: "Select relationship type...",
        label: RelationshipTypeSelect.defaultProps.label,
        multi: props.multi,
    })
RelationshipTypeSelect.defaultProps = {
    label: "display_text",
}

export const SupporterSourceSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.grassroots.types.UTMSourceType.region_supported_items()
            .filter((item) => item.relevant_to_contacts)
            .sort(DjangIO.valueSort),
        placeholder: "Select multiple sources...",
        label: SupporterSourceSelect.defaultProps.label,
        multi: props.multi,
    })
SupporterSourceSelect.defaultProps = {
    label: "label",
}

export const SupporterActionTypeSelect = (props) => {
    return genericEnumSelect({
        ...props,
        options: props.enumItems,
        placeholder: "Select multiple actions...",
        label: props.label,
        multi: true,
    })
}
SupporterActionTypeSelect.defaultProps = {
    label: "label",
}

export const PopulationRangesSelect = (props) => {
    return genericEnumSelect({
        ...props,
        options: props.enumItems,
        placeholder: "Select population ranges...",
        label: props.label,
        multi: true,
        closeMenuOnSelect: false,
    })
}
PopulationRangesSelect.defaultProps = {
    label: "label",
}

export const InteractionTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        // take the options stored on the organization itself and convert them into the same
        // format as the other enums
        options: Userdata.internal_interaction_types.map((item) =>
            Object.assign({}, DjangIO.app.userdata.types.InteractionType.by_value(item), {
                display_text: DjangIO.app.userdata.types.InteractionType.by_value(item).label,
            }),
        ),
        placeholder: "Select Interaction type...",
        label: InteractionTypeSelect.defaultProps.label,
        multi: props.multi,
    })
InteractionTypeSelect.defaultProps = {
    label: "display_text",
}

export const DurationSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.userdata.types.PossibleDurations.items()
            .filter((item) => item.key !== "none")
            .sort((a, b) => a.order - b.order)
            .map((item) => ({
                ...item,
                display_text: item.label,
            })),
        placeholder: "Select Interaction type...",
        label: DurationSelect.defaultProps.label,
        multi: props.multi,
    })
DurationSelect.defaultProps = {
    label: "display_text",
}

export const BillStatusSelect = (props) => {
    // filtering < 92 || > 101 removes nomination bill statuses for the time being
    let options = DjangIO.app.bill.status.BillStatus.items().filter((item) => item.value < 92 || item.value > 101)
    if (Userdata.isOnlyStatesMode()) {
        options = options.filter((option) => option.state_label)
    }

    return genericEnumSelect({
        ...props,
        options,
        placeholder: "Choose statuses...",
        multi: props.multi,
        label: BillStatusSelect.defaultProps.label,
    })
}
BillStatusSelect.defaultProps = {
    label: Userdata.isOnlyStatesMode() ? "state_label" : "label",
}

export const RegulationStatusSelect = (props) => {
    const DetailedStatusExclude = [
        DjangIO.app.reg.types.DetailedRegStatus.oira_prerule_review.value,
        DjangIO.app.reg.types.DetailedRegStatus.oira_proposal_review.value,
        DjangIO.app.reg.types.DetailedRegStatus.oira_final_review.value,
    ]

    return genericEnumSelect({
        ...props,
        options: DjangIO.app.reg.types.DetailedRegStatus.region_supported_items()
            .filter((item) => !DetailedStatusExclude.includes(item.value))
            .sort((a, b) => a.value - b.value),
        placeholder: "Choose statuses...",
        multi: props.multi,
        label: "label",
    })
}

export const RegionSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.models.Region.items()
            .filter((item) => isRegionEnabled(item))
            .filter((item) => {
                if (item.abbrev === "us") {
                    return Userdata.isFederalMode()
                } else if (item.abbrev.length === 2 && item.abbrev !== "eu") {
                    return Userdata.isStatesMode()
                } else if (item.abbrev.indexOf("local") !== -1) {
                    return Userdata.isLocalMode()
                }

                return true
            })
            .sort((a, b) => {
                if (a.label > b.label) {
                    return 1
                } else {
                    return -1
                }
            }),
        placeholder: props.multi ? "Choose regions..." : "Choose a region...",
        multi: props.multi,
        label: RegionSelect.defaultProps.label,
    })
RegionSelect.defaultProps = {
    label: "region_name",
}

export const AllRegionSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.models.Region.items().sort((a, b) => {
            if (a.label > b.label) {
                return 1
            } else {
                return -1
            }
        }),
        placeholder: props.multi ? "Choose regions..." : "Choose a region...",
        multi: props.multi,
        label: AllRegionSelect.defaultProps.label,
    })
AllRegionSelect.defaultProps = {
    label: "region_name",
}

export const ExpandedRegionSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: [
            {
                region_name: "All Regions",
                value: "all",
            },
            {
                region_name: "All State Regions",
                value: "state",
            },
            {
                region_name: "All Local Regions",
                value: "local",
            },
        ].concat(
            DjangIO.app.models.Region.items()
                .filter((item) => Userdata.allowed_regions_values.includes(item.value))
                .map((item) => ({ region_name: item.region_name, value: item.value })),
        ),
        placeholder: "Choose regions...",
        multi: props.multi,
        label: ExpandedRegionSelect.defaultProps.label,
    })
ExpandedRegionSelect.defaultProps = {
    label: "region_name",
}

export const NoteInteractionTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.userdata.types.InteractionType.items()
            .filter((type) => Boolean(type.organization_id) && !type.archived)
            .sort(sortByLabel),
        placeholder: "Choose interaction types...",
        multi: props.multi,
        label: "label",
    })

export const NoteDonationTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.userdata.types.InteractionType.region_supported_items()
            .filter((item) => item.should_display_amount)
            .sort(sortByLabel),
        placeholder: "Choose donation types...",
        multi: props.multi,
        label: "label",
    })

export const NoteRelationshipTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.userdata.types.RelationshipType.region_supported_items().sort(sortByLabel),
        placeholder: "Choose relationship types...",
        multi: props.multi,
        label: "label",
    })

const regionSupportedEnumItems = DjangIO.app.models.QuorumDataType.region_supported_items()

const commentEnumItems = [
    DjangIO.app.models.QuorumDataType.bill,
    DjangIO.app.models.QuorumDataType.amendment,
    DjangIO.app.models.QuorumDataType.document,
    DjangIO.app.models.QuorumDataType.project,
    DjangIO.app.models.QuorumDataType.person,
    DjangIO.app.models.QuorumDataType.press_contact,
    DjangIO.app.models.QuorumDataType.supporter,
    DjangIO.app.models.QuorumDataType.public_organization,
    DjangIO.app.models.QuorumDataType.staffer,
    DjangIO.app.models.QuorumDataType.vote,
    DjangIO.app.models.QuorumDataType.regulation,
    DjangIO.app.models.QuorumDataType.media,
    DjangIO.app.models.QuorumDataType.send_email,
].filter((item) => regionSupportedEnumItems.includes(item))

export const NoteCommentTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: commentEnumItems.sort(sortByLabel),
        placeholder: "Choose comment types...",
        multi: props.multi,
        label: "singular",
    })

export const ReceiptsSupportedRegionSelect = (props) =>
    genericEnumSelect({
        ...props,
        placeholder: "Choose regions, states, or territories",
        multi: props.multi,
        label: "region_name",
    })

export const BillSupportedRegionSelect = (props) =>
    genericEnumSelect({
        ...props,
        placeholder: "Choose regions, states, or territories",
        multi: props.multi,
        label: "region_name",
    })

export const BillCommitteeTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.committee.models.CommitteeKeyword.region_supported_items(),
        placeholder: "Choose type of committee...",
        multi: props.multi,
        label: "label",
    })

export const InitiativeDetailedStatusSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: props.enumItems,
        placeholder: "Choose detailed initiative stage...",
        multi: props.multi,
        label: "label",
    })

export const CampaignTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.grassroots.campaign.types.CampaignType.region_supported_items()
            .filter((item) => item.is_enabled_for_campaign_form)
            .sort(DjangIO.valueSort),
        placeholder: "Choose type of campaign...",
        multi: props.multi,
        label: "label",
    })

export const OfficialApointedByTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.person.types.AppointedByType.region_supported_items().sort(DjangIO.valueSort),
        placeholder: "Choose appointed by type...",
        multi: props.multi,
        label: "label",
    })

const yearSelect = (options, props, placeholder = "Choose years...") =>
    genericEnumSelect({
        ...props,
        options,
        placeholder,
        multi: props.multi,
        label: "display_text",
    })

export const BillYearSelect = (props) => yearSelect(getBillYearSelectOptions(), props)

export const ElectionYearSelect = (props) =>
    yearSelect(getElectionYearSelectOptions(), props, "Choose election years...")

export const BillDateListSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.events.models.DateRangeType.region_supported_items()
            .filter(
                (item) =>
                    ![
                        DjangIO.app.events.models.DateRangeType.future.value,
                        DjangIO.app.events.models.DateRangeType.next_month.value,
                        DjangIO.app.events.models.DateRangeType.next_week.value,
                    ].includes(item.value),
            )
            .sort(DjangIO.orderSort),
        placeholder: Userdata.isOnlyEUMode() ? "Choose Date Proposed..." : "Choose Date Introduced...",
        multi: props.multi,
        label: "label",
    })

export const DateListSelect = (props) =>
    genericEnumSelect({
        ...props,
        options:
            props.customOptions ||
            props.options ||
            DjangIO.app.events.models.DateRangeType.region_supported_items().sort(DjangIO.orderSort),
        placeholder: "Choose date range...",
        multi: props.multi,
        label: "label",
    })

export const DateListNoFutureSelect = (props) =>
    genericEnumSelect({
        ...props,
        options:
            props.customOptions ||
            props.options ||
            DjangIO.app.events.models.DateRangeType.region_supported_items()
                .filter((item) => !item.is_future)
                .sort(DjangIO.orderSort),
        placeholder: "Choose date range...",
        multi: props.multi,
        label: "label",
    })

export const DateListNoPastSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.events.models.DateRangeType.region_supported_items()
            .filter(
                (item) =>
                    item.value !== DjangIO.app.events.models.DateRangeType.this_quarter.value &&
                    item.value !== DjangIO.app.events.models.DateRangeType.this_year.value,
            )
            .sort(DjangIO.orderSort),
        placeholder: "Choose date range...",
        multi: props.multi,
        label: "label",
    })

export const LegislatorOnCommitteeTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.committee.models.CommitteeKeyword.region_supported_items().sort(sortByLabel),
        placeholder: "Choose type of committee...",
        multi: props.multi,
        label: "label",
    })

const sortByLabel = (a, b) => {
    if (a.label > b.label) {
        return 1
    } else if (a.label < b.label) {
        return -1
    } else {
        return 0
    }
}

export const ChamberSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.models.CongressChamber.items()
            .filter((item) => item.show_in_enumeration)
            .sort(sortByLabel),
        placeholder: props.multi ? "Choose chambers..." : "Choose a chamber...",
        multi: props.multi,
    })

export const InternationalCountrySelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.models.Region.items()
            .filter((item) => item.is_international_region)
            .filter((item) => item.international_region_ready)
            .sort(sortByLabel),
        placeholder: props.multi ? "Choose countries..." : "Choose a country...",
        multi: props.multi,
        label: InternationalCountrySelect.defaultProps.label,
    })
InternationalCountrySelect.defaultProps = {
    label: "region_name",
}

export const StateSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.models.Region.items()
            .filter((item) => item.is_state_region)
            .sort(sortByLabel),
        placeholder: props.multi ? "Choose regions..." : "Choose a region...",
        multi: props.multi,
        label: StateSelect.defaultProps.label,
    })
StateSelect.defaultProps = {
    label: "region_name",
}

export const LocalSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.models.Region.items()
            .filter((item) => item.abbrev.includes("local"))
            .filter((item) => item.show_in_selector)
            .sort(sortByLabel),
        placeholder: props.multi ? "Choose regions..." : "Choose a region...",
        multi: props.multi,
        label: LocalSelect.defaultProps.label,
    })
LocalSelect.defaultProps = {
    label: "region_name",
}

export const LocalSelectExceptDCLocal = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.models.Region.items()
            .filter((item) => item.abbrev.includes("local"))
            .filter((item) => item.show_in_selector)
            .filter((item) => item.abbrev !== "dc_local")
            .sort(sortByLabel),
        placeholder: props.multi ? "Choose regions..." : "Choose a region...",
        multi: props.multi,
        label: LocalSelectExceptDCLocal.defaultProps.label,
    })
LocalSelectExceptDCLocal.defaultProps = {
    label: "region_name",
}

export const AllButLocalSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.models.Region.items()
            .filter((item) => !item.abbrev.includes("local"))
            .sort(sortByLabel),
        placeholder: props.multi ? "Choose regions..." : "Choose a region...",
        multi: props.multi,
        label: AllButLocalSelect.defaultProps.label,
    })
AllButLocalSelect.defaultProps = {
    label: "region_name",
}

export const AllowedRegionSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: props.options.sort(sortByLabel),
        placeholder: props.multi ? "Choose regions..." : "Choose a region...",
        multi: props.multi,
        label: AllowedRegionSelect.defaultProps.label,
    })
AllowedRegionSelect.defaultProps = {
    label: "region_name",
}

export const AccountTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.userdata.types.AccountType.items(),
        placeholder: props.multi ? "Choose account types..." : "Choose account type...",
        multi: props.multi,
    })

export const OnboardingTypeSelect = (props) => {
    const options = DjangIO.app.userdata.types.OnboardingType.items().sort(DjangIO.valueSort)
    const nonDisabledOptions = options.filter((item) => !item.disabled)
    const disabledOptions = options.filter((item) => item.disabled)

    return genericEnumSelect({
        ...props,
        options: [...nonDisabledOptions, ...disabledOptions],
        placeholder: props.multi ? "Choose onboarding types..." : "Choose onboarding type...",
        multi: props.multi,
    })
}

export const OrganizationTeamSelect = (props) => {
    const options = props.options
    const nonDisabledOptions = options.filter((item) => !item.disabled)
    const disabledOptions = options.filter((item) => item.disabled)

    return genericEnumSelect({
        ...props,
        options: [...nonDisabledOptions, ...disabledOptions],
        placeholder: "Choose organization team...",
    })
}

export const DegreeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.person.types.TerminalDegree.items(),
        placeholder: "Choose terminal degree...",
        multi: props.multi,
    })

export const GenderSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.person.types.Gender.items()
            .sort(DjangIO.valueSort)
            .filter((item) => ["Male", "Female"].includes(item.label)),
        placeholder: "Choose gender...",
        multi: props.multi,
    })

export const LanguageSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.document.types.Language.items().sort((a, b) => a.label.localeCompare(b.label)),
        placeholder: "Choose language...",
        multi: props.multi,
    })

export const EthnicitySelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.person.types.Ethnicity.items(),
        placeholder: "Choose ethnicity...",
        multi: props.multi,
    })

export const ReligionSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.person.types.Religion.region_supported_items(),
        placeholder: "Choose religion...",
        multi: props.multi,
    })
export const PrefixSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.grassroots.enums.PrefixType.items().sort((a, b) => a.order - b.order),
        placeholder: "",
        multi: props.multi,
    })

export const SuffixSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.grassroots.enums.SuffixType.items().sort((a, b) => a.order - b.order),
        placeholder: "",
        multi: props.multi,
    })

export const CustomChosenOptionsTagSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: props.customOptions,
        placeholder: "Choose the desired option...",
        multi: props.multi,
    })

export const FontSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: Fonts.map((option) => ({
            ...option,
            label: option.id,
            value: option.id,
        })),
        placeholder: "Choose a font...",
    })

export const PrivacySelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.userdata.types.PrivacyType.items().filter((item) =>
            ["public", "private"].includes(item.key),
        ),
        placeholder: "Choose privacy...",
        label: PrivacySelect.defaultProps.label,
    })
PrivacySelect.defaultProps = {
    label: "board_display",
}

export const ListTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        placeholder: "Choose type of list...",
        label: ListTypeSelect.defaultProps.label,
    })
ListTypeSelect.defaultProps = {
    label: "plural",
}

const getCanViewPACTransactions = () =>
    !isFeatureEnabled("ff_pac_permissions_one") || Userdata.can_view_pac_disbursements || Userdata.can_view_pac_receipts

export const NewListTypeSelect = (props) =>
    genericEnumSelect({
        placeholder: "Choose type of list...",
        ...props,
        label: ListTypeSelect.defaultProps.label,
    })
NewListTypeSelect.defaultProps = {
    options: DjangIO.app.models.QuorumDataType.region_supported_items()
        .filter(permissionFilter)
        .filter((qdt) => qdt.custom_lists)
        .filter((qdt) => {
            if (
                qdt.value === DjangIO.app.models.QuorumDataType.send_email_archived.value &&
                !isFeatureEnabled("ff_send_email_archived_flv")
            ) {
                return false
            }

            if (qdt.value === DjangIO.app.models.QuorumDataType.transaction.value) {
                return getCanViewPACTransactions()
            }

            if (
                checkHasAllPermissions([
                    DjangIO.app.models.PermissionType.campaign_finance,
                    DjangIO.app.models.PermissionType.can_access_campaign_finance,
                ])
            ) {
                return true
            } else if (
                qdt.value === DjangIO.app.models.QuorumDataType.receipt.value ||
                qdt.value === DjangIO.app.models.QuorumDataType.disbursement.value
            ) {
                return false
            }

            return true
        })
        .sort((a, b) => sortBySearchOrder(a, b)),
    label: "plural",
}

export const NumberWidgetSelect = (props) =>
    genericEnumSelect({
        placeholder: "Choose type of count...",
        ...props,
        label: ListTypeSelect.defaultProps.label,
    })
NumberWidgetSelect.defaultProps = {
    options: DjangIO.app.models.QuorumDataType.region_supported_items()
        .filter(permissionFilter)
        .filter((qdt) => qdt.custom_lists)
        .filter((qdt) => {
            if (
                qdt.value === DjangIO.app.models.QuorumDataType.send_email_archived.value &&
                !isFeatureEnabled("ff_send_email_archived_flv")
            ) {
                return false
            }

            if (qdt.value === DjangIO.app.models.QuorumDataType.transaction.value) {
                return getCanViewPACTransactions()
            }

            return true
        })
        .filter(
            (qdt) =>
                ![
                    DjangIO.app.models.QuorumDataType.disbursement.value,
                    DjangIO.app.models.QuorumDataType.receipt.value,
                ].includes(qdt.value),
        )
        .sort((a, b) => sortBySearchOrder(a, b)),
    label: "plural",
}

export const PieVisualizationTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
    })
PieVisualizationTypeSelect.defaultProps = {
    options: DjangIO.app.models.QuorumDataType.region_supported_items()
        .filter(permissionFilter)
        // return all of the qdts that appear in search
        .filter((qdt) => qdt.advanced_search)
        .filter((qdt) => {
            if (
                qdt.value === DjangIO.app.models.QuorumDataType.send_email_archived.value &&
                !isFeatureEnabled("ff_send_email_archived_flv")
            ) {
                return false
            }

            if (qdt.value === DjangIO.app.models.QuorumDataType.transaction.value) {
                return getCanViewPACTransactions()
            }

            return true
        })
        .filter(
            (qdt) =>
                ![
                    DjangIO.app.models.QuorumDataType.disbursement.value,
                    DjangIO.app.models.QuorumDataType.receipt.value,
                ].includes(qdt.value),
        )
        .sort((a, b) => sortBySearchOrder(a, b)),
    label: "plural",
}

export const DateFieldSelect = (props) =>
    genericEnumSelect({
        ...props,
    })
DateFieldSelect.defaultProps = {
    options: DjangIO.app.models.QuorumDataType.region_supported_items()
        .filter(permissionFilter)
        .filter((qdt) => DjangIO.app.widgets.types.DateFields.valid_qdts.includes(qdt.value))
        .filter((qdt) =>
            qdt.value === DjangIO.app.models.QuorumDataType.transaction.value ? getCanViewPACTransactions() : true,
        )
        .sort((a, b) => sortBySearchOrder(a, b)),
    label: "plural",
}

export const MapVisualizationTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
    })
MapVisualizationTypeSelect.defaultProps = {
    options: DjangIO.app.models.QuorumDataType.region_supported_items()
        .filter(permissionFilter)
        .filter((t) => {
            if (
                t.value === DjangIO.app.models.QuorumDataType.send_email_archived.value &&
                !isFeatureEnabled("ff_send_email_archived_flv")
            ) {
                return false
            }
            return true
        })
        .sort((a, b) => sortBySearchOrder(a, b)),
    label: "plural",
}

export const FrequencyTupleFieldSelect = (props) =>
    genericEnumSelect({
        ...props,
    })
FrequencyTupleFieldSelect.defaultProps = {
    // FrequencyTupleFieldSelect with its default options (below)
    // used in
    // app/static/frontend/dashboards/components/WidgetEditFormSidebars/VisualizationFormSections/BarFormSection.jsx

    // FrequencyTupleFieldSelect with overridden options
    // used in
    // app/static/frontend/dashboards/components/WidgetEditFormSidebars/VisualizationFormSections/MapFormSection{Advanced}.jsx
    // since some of its .filter conditions require a conditional props.frequencyTupleField variable
    options: DjangIO.app.models.QuorumDataType.region_supported_items()
        .filter(permissionFilter)
        .filter((qdt) => DjangIO.app.widgets.types.FrequencyTupleFields.valid_bar_qdts.includes(qdt.value))
        .filter((qdt) =>
            qdt.value === DjangIO.app.models.QuorumDataType.transaction.value ? getCanViewPACTransactions() : true,
        )
        .sort((a, b) => sortBySearchOrder(a, b)),
    label: "plural",
}

export const AlertWeekdaySelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.userdata.types.Weekdays.items().sort(DjangIO.valueSort),
        placeholder: "Choose weekday...",
        label: AlertWeekdaySelect.defaultProps.label,
    })
AlertWeekdaySelect.defaultProps = {
    label: "short_label",
}

export const SheetTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        options: DjangIO.app.models.QuorumDataType.items()
            .filter((item) => item.sheets)
            .sort((a, b) => sortBySearchOrder(a, b)),
        label: SheetTypeSelect.defaultProps.label,
    })
SheetTypeSelect.defaultProps = {
    label: "plural",
}

export const PersonaSelect = (props) =>
    genericEnumSelect({
        ...props,
        dataCy: "persona-select",
        // options are calculated in component
        placeholder: "Choose Persona...",
    })

export const DefaultSearchDataTypeSelect = (props) =>
    genericEnumSelect({
        ...props,
        // options are calculated in component
        placeholder: "Choose default search type...",
        label: DefaultSearchDataTypeSelect.defaultProps.label,
    })
DefaultSearchDataTypeSelect.defaultProps = {
    label: "singular",
}

// This is not currently in use; If you want to use it, make sure it is using LegislativeStanceCustomLabels
export const StanceSelect = (props) => genericEnumSelect({ ...props })
StanceSelect.defaultProps = {
    options: DjangIO.app.userdata.types.LegislativeStanceType.items().sort(DjangIO.valueSort),
    placeholder: "Choose a stance...",
}

export const PrioritySelect = (props) => genericEnumSelect({ ...props })
PrioritySelect.defaultProps = {
    options: DjangIO.app.userdata.types.LegislativePriorityType.items().sort(DjangIO.valueSort),
    placeholder: "Choose a priority...",
}

export const ReactGridLayoutTypeSelect = (props) => genericEnumSelect({ ...props })
ReactGridLayoutTypeSelect.defaultProps = {
    options: DjangIO.app.widgets.types.ReactGridLayoutTypes.items().sort(DjangIO.valueSort),
    placeholder: "Choose a layout type...",
}

export const PacOrgEntityTypeSelect = (props) => genericEnumSelect({ ...props })
PacOrgEntityTypeSelect.defaultProps = {
    options: DjangIO.app.pac.types.EntityType.items().filter((item) => item.user_selectable_pac_org_type),
    placeholder: "Choose a PAC type...",
}

export const MinorRoleTypeSelect = (props) => genericEnumSelect({ ...props })
MinorRoleTypeSelect.defaultProps = {
    options: DjangIO.app.person.newtypes.MinorRoleType.region_supported_items()
        .filter(
            (minorRoleType) =>
                DjangIO.app.person.newtypes.MajorRoleType.by_value(minorRoleType.parent).parent ===
                DjangIO.app.person.newtypes.MetaRoleType.media_meta.value,
        )
        .sort(sortByLabel),
    placeholder: "Filter for Title...",
}

export const FrequencySelect = (props) => genericEnumSelect({ ...props })
FrequencySelect.defaultProps = {
    options: DjangIO.app.media_monitoring.types.IntradoFrequency.items().sort(DjangIO.valueSort),
    placeholder: "Choose frequency...",
}

export const CirculationSelect = (props) => genericEnumSelect({ ...props })
CirculationSelect.defaultProps = {
    options: DjangIO.app.media_monitoring.types.IntradoCirculation.items().sort(DjangIO.valueSort),
    placeholder: "Filter for circulation...",
}

export const PayrollFrequencyTypeSelect = (props) => genericEnumSelect(props)
PayrollFrequencyTypeSelect.defaultProps = {
    options: DjangIO.app.ledger.types.PayrollFrequencyType.items().sort(DjangIO.valueSort),
    placeholder: "Choose a Payroll Frequency...",
}

export const SessionTimeoutSelect = (props) => genericEnumSelect(props)
SessionTimeoutSelect.defaultProps = {
    options: [
        { label: "Default (1 Year)", value: null },
        { label: "15 Minutes", value: 15 * 60 },
        { label: "30 Minutes", value: 30 * 60 },
        { label: "1 Hour", value: 1 * 60 * 60 },
        { label: "2 Hours", value: 2 * 60 * 60 },
        { label: "3 Hours", value: 3 * 60 * 60 },
        { label: "6 Hours", value: 6 * 60 * 60 },
        { label: "12 Hours", value: 12 * 60 * 60 },
        { label: "24 Hours", value: 1 * 24 * 60 * 60 },
        { label: "48 Hours", value: 2 * 24 * 60 * 60 },
        { label: "72 Hours", value: 3 * 24 * 60 * 60 },
        { label: "1 Week", value: 1 * 7 * 24 * 60 * 60 },
        { label: "2 Weeks", value: 2 * 7 * 24 * 60 * 60 },
        { label: "3 Weeks", value: 3 * 7 * 24 * 60 * 60 },
        { label: "1 Month", value: 1 * 30 * 24 * 60 * 60 },
        { label: "2 Months", value: 2 * 30 * 24 * 60 * 60 },
        { label: "3 Months", value: 3 * 30 * 24 * 60 * 60 },
        { label: "6 Months", value: 6 * 30 * 24 * 60 * 60 },
    ],
}

export const FecPartySelect = (props) => genericEnumSelect({ ...props })
FecPartySelect.defaultProps = {
    options: DjangIO.app.pac.types.FecParty.region_supported_items().sort(
        (a, b) => a.major_party_order - b.major_party_order,
    ),
    placeholder: "Filter for FEC Party...",
}

export const LedgerAccountTypeSelect = (props) => genericEnumSelect({ ...props })
LedgerAccountTypeSelect.defaultProps = {
    options: DjangIO.app.ledger.types.LedgerAccountType.region_supported_items(),
    placeholder: "Filter for Transaction Type...",
}

export const TransactionMethodTypeSelect = (props) => genericEnumSelect({ ...props })
TransactionMethodTypeSelect.defaultProps = {
    options: DjangIO.app.ledger.types.TransactionMethodType.region_supported_items(),
    placeholder: "Choose methods...",
}

export const FecCommitteeElectionTypeSelect = (props) => genericEnumSelect({ ...props })
FecCommitteeElectionTypeSelect.defaultProps = {
    options: DjangIO.app.pac.types.PoliticalCommitteeType.region_supported_items(),
    placeholder: "Filter for PAC Election Type...",
}

export const ElectionYearSelectFilter = (props) => genericEnumSelect({ ...props })
ElectionYearSelectFilter.defaultProps = {
    options: generateElectionYearEnumItems({}),
    placeholder: "Enter a year...",
}

export const PacClassicPacLimitTypeSelect = (props) => genericEnumSelect({ ...props })
PacClassicPacLimitTypeSelect.defaultProps = {
    options: DjangIO.app.pac_classic.types.PacClassicPacLimitType.region_supported_items(),
    placeholder: "Choose a PAC Limit Type...",
}

export const PacClassicPartyTypeSelect = (props) => genericEnumSelect({ ...props })
PacClassicPartyTypeSelect.defaultProps = {
    placeholder: "Choose a Political party...",
}

export const PacClassicPacReportCodeSelect = (props) => genericEnumSelect({ ...props })
PacClassicPacReportCodeSelect.defaultProps = {
    options: DjangIO.app.pac_classic.types.PacClassicPacReportCode.region_supported_items(),
    placeholder: "Choose a PAC Report Code Type...",
}
